// React imports
import React from 'react';

// Third-party imports
import clsx from 'clsx';

// Types
import {
    DiscountType,
    DiscountableType,
    SaveConfiguredDiscount,
    ApplicationType,
    ConfiguredDiscountItemAttribute
} from '@spike/discount-model';

// UI Components
import { OverFullWindow } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup';
import { ValueTypeSelector } from 'components/UI/V3/Forms/ValueTypeSelector';
import TagsSelector from 'components/UI/V3/TagsSelector';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';

// Local Components
import { ServiceCategorySelector, ServiceCategorySkeleton } from './ServiceCategorySelector';
import { ItemSelectionDrawer } from 'components/UI/V3/ItemSelectionDrawer/ItemSelectionDrawer';
import TieredValueTypeSelector from './TieredValueTypeSelector';

// Context
import { useDiscounts } from '../DiscountsProvider';

// Hooks
import { useApiClientWrapper } from 'hooks';
import useServiceCategories from 'hooks/useServiceCategories';
import {
    useDiscountForm,
    useDiscountValidation,
    useDrawerConfigs,
    useItemDisplayConfigs,
    useItemSelection,
    useServiceCategoryConfigs
} from './hooks';

// Utils
import { convertToEditItemsAttributes } from '../Utils';

// Styles
import styles from './AddDiscount.module.scss';
import { getClientTagsThunk } from '@spike/marketplace-action';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { MarketplaceClientTags } from '@spike/marketplace-model';

interface AddDiscountProps {
    onClose: () => void;
    onSuccess: (discount: SaveConfiguredDiscount) => void;
}

export const AddDiscount: React.FC<AddDiscountProps> = ({ onClose, onSuccess }) => {
    const { hasGrooming, hasDaycare, hasBoarding, hasTransportation, hasEvaluation } = useServiceCategories();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const {
        editDiscount,
        originalDiscount,
        discountMode,
        groomingServices,
        daycareServices,
        boardingServices,
        transportationServices,
        evaluationServices,
        addons,
        products,
        isServicesLoading,
        saveDiscount: saveDiscountFromContext
    } = useDiscounts();

    const isViewMode = discountMode === 'view';
    const [showExitPrompt, setShowExitPrompt] = React.useState(false);
    const [isFormPopulated, setIsFormPopulated] = React.useState(false);

    const {
        discountType,
        discountName,
        selectedClientTags,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedTransportationServices,
        selectedEvaluationServices,
        selectedAddons,
        selectedProducts,
        applyDiscountTo,
        discountValue,
        tieredDiscountType,
        tieredDiscountValues,
        setDiscountType,
        setDiscountName,
        setSelectedClientTags,
        setSelectedGroomingServices,
        setSelectedDaycareServices,
        setSelectedBoardingServices,
        setSelectedAddons,
        setSelectedTransportationServices,
        setSelectedEvaluationServices,
        setSelectedProducts,
        setApplyDiscountTo,
        setDiscountValue,
        setTieredDiscountType,
        setTieredDiscountValues
    } = useDiscountForm({
        initialDiscountName: editDiscount?.name,
        editDiscount: editDiscount
    });
    const store = useSelector<RootState, MarketplaceClientTags>(state => state.marketplace.clientTags);
    const clientTags = React.useMemo(() => {
        return [...store.positive].sort((a, b) => {
            const sourceOrderA = a.source === 'default' ? 0 : 1;
            const sourceOrderB = b.source === 'default' ? 0 : 1;

            if (sourceOrderA !== sourceOrderB) {
                return sourceOrderA - sourceOrderB;
            }

            return a.name.localeCompare(b.name);
        });
    }, [store.positive]);

    const [clientTagsLoaded, setClientTagsLoaded] = React.useState(false);
    const initialClientTagsRef = React.useRef(clientTags);

    React.useEffect(() => {
        dispatch(getClientTagsThunk(apiClientWrapper));
    }, []);

    React.useEffect(() => {
        if (clientTags !== initialClientTagsRef.current) {
            setClientTagsLoaded(true);
        }
    }, [clientTags]);

    // Sync applyDiscountTo when switching to Client Tag
    React.useEffect(() => {
        if (discountType === 'Client Tag' && applyDiscountTo !== 'All Pets') {
            setApplyDiscountTo('All Pets');
        }
    }, [discountType, applyDiscountTo, setApplyDiscountTo]);

    // Populate form when editing a discount (only once)
    React.useEffect(() => {
        if (!editDiscount || isServicesLoading || isFormPopulated) return;

        // Set client tags if applicable
        const isClientTag = editDiscount.applicationType === ApplicationType.CUSTOMER_TAG;

        // Wait for fresh client tags before populating to avoid stale names
        if (isClientTag && !clientTagsLoaded) return;

        if (isClientTag && editDiscount.customerTagList) {
            const tags = editDiscount.customerTagList
                .map(tagId => {
                    const matchingTag = clientTags.find(tag => tag.id === tagId);
                    return matchingTag ? { id: matchingTag.id, name: matchingTag.name } : null;
                })
                .filter((tag): tag is { id: string; name: string } => tag !== null);
            setSelectedClientTags(tags);
        }

        // Parse and set selected items from configuredDiscountItemsAttributes
        if (editDiscount.configuredDiscountItemsAttributes) {
            const groomingIds: number[] = [];
            const daycareIds: number[] = [];
            const boardingIds: number[] = [];
            const transportationIds: number[] = [];
            const evaluationIds: number[] = [];
            const addonIds: number[] = [];
            const productIds: number[] = [];

            editDiscount.configuredDiscountItemsAttributes.forEach(item => {
                // Skip items without discountableId (these are edit items with only id)
                if (!item.discountableId) return;

                if (item.discountableType === DiscountableType.SERVICE) {
                    // Check if it's an addon service first (addon services are also marked as 'Service')
                    const isAddon = addons.some(a => a.id === item.discountableId);

                    if (isAddon) {
                        addonIds.push(item.discountableId);
                    } else {
                        // Then check which service category this belongs to
                        const isGrooming = groomingServices.some(s => s.id === item.discountableId);
                        const isDaycare = daycareServices.some(s => s.id === item.discountableId);
                        const isBoarding = boardingServices.some(s => s.id === item.discountableId);
                        const isTransportation = transportationServices.some(s => s.id === item.discountableId);
                        const isEvaluation = evaluationServices.some(s => s.id === item.discountableId);

                        if (isGrooming) groomingIds.push(item.discountableId);
                        if (isDaycare) daycareIds.push(item.discountableId);
                        if (isBoarding) boardingIds.push(item.discountableId);
                        if (isTransportation) transportationIds.push(item.discountableId);
                        if (isEvaluation) evaluationIds.push(item.discountableId);
                    }
                } else if (item.discountableType === DiscountableType.ADDON_SERVICE) {
                    addonIds.push(item.discountableId);
                } else if (item.discountableType === DiscountableType.BUSINESS_PRODUCT) {
                    productIds.push(item.discountableId);
                }
            });

            const selectedGrooming = groomingServices.filter(s => groomingIds.includes(s.id!));
            const selectedDaycare = daycareServices.filter(s => daycareIds.includes(s.id!));
            const selectedBoarding = boardingServices.filter(s => boardingIds.includes(s.id!));
            const selectedTransportation = transportationServices.filter(s => transportationIds.includes(s.id!));
            const selectedEvaluation = evaluationServices.filter(s => evaluationIds.includes(s.id!));
            const selectedAddonsFiltered = addons.filter(a => addonIds.includes(a.id!));
            const selectedProductsFiltered = products.filter(
                p => p.businessProduct?.productId && productIds.includes(p.businessProduct.productId)
            );

            setSelectedGroomingServices(selectedGrooming);
            setSelectedDaycareServices(selectedDaycare);
            setSelectedBoardingServices(selectedBoarding);
            setSelectedTransportationServices(selectedTransportation);
            setSelectedEvaluationServices(selectedEvaluation);
            setSelectedAddons(selectedAddonsFiltered);
            setSelectedProducts(selectedProductsFiltered);
        }

        // Determine applyDiscountTo and set discount values
        const values = editDiscount.values || {};
        const discountTypeValue: 'percentage' | 'amount' =
            editDiscount.discountType === DiscountType.PERCENTAGE ? 'percentage' : 'amount';
        const isPercentage = editDiscount.discountType === DiscountType.PERCENTAGE;

        // Helper to convert backend value to UI value
        // Backend sends percentages as decimals (0.1 = 10%)
        const convertValue = (value: number): number => {
            return isPercentage ? value * 100 : value;
        };

        if (values.allPets !== undefined) {
            setApplyDiscountTo('All Pets');
            setDiscountValue({
                type: discountTypeValue,
                value: convertValue(values.allPets)
            });
        } else if (values.additionalPets !== undefined) {
            setApplyDiscountTo('Additional Pet');
            setDiscountValue({
                type: discountTypeValue,
                value: convertValue(values.additionalPets)
            });
        } else {
            // Tiered discount
            setApplyDiscountTo('Tiered');
            setTieredDiscountType(discountTypeValue);

            // Parse tiered values (backend sends numeric keys: '0', '1', '2', etc.)
            const tieredValues: number[] = [];
            // Get all numeric keys and sort them
            const numericKeys = Object.keys(values)
                .filter(key => !isNaN(Number(key)))
                .map(key => Number(key))
                .sort((a, b) => a - b);

            for (const index of numericKeys) {
                const petValue = values[String(index)];
                if (petValue !== undefined) {
                    tieredValues.push(convertValue(petValue));
                }
            }

            // Set tiered discounts if any were found
            if (tieredValues.length > 0) {
                setTieredDiscountValues(tieredValues);
            }
        }

        // Mark form as populated to prevent re-running this effect
        setIsFormPopulated(true);
    }, [
        editDiscount,
        isServicesLoading,
        isFormPopulated,
        clientTagsLoaded,
        clientTags,
        groomingServices,
        daycareServices,
        boardingServices,
        transportationServices,
        evaluationServices,
        addons,
        products,
        setSelectedClientTags,
        setSelectedGroomingServices,
        setSelectedDaycareServices,
        setSelectedBoardingServices,
        setSelectedAddons,
        setSelectedTransportationServices,
        setSelectedEvaluationServices,
        setSelectedProducts,
        setApplyDiscountTo,
        setDiscountValue,
        setTieredDiscountType,
        setTieredDiscountValues
    ]);

    // Form validation
    const { isFormValid, tierErrors } = useDiscountValidation({
        discountName,
        discountType,
        selectedClientTags,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedTransportationServices,
        selectedEvaluationServices,
        selectedAddons,
        selectedProducts,
        applyDiscountTo,
        discountValue,
        tieredDiscountValues
    });

    // Display configurations
    const { serviceDisplay, addonDisplay, productDisplay } = useItemDisplayConfigs();

    // Item selection state management using custom hook
    const groomingSelection = useItemSelection({
        items: groomingServices,
        selectedItems: selectedGroomingServices,
        onSelectedItemsChange: setSelectedGroomingServices,
        filterFn: (service, query) => service.name.toLowerCase().includes(query.toLowerCase())
    });

    const daycareSelection = useItemSelection({
        items: daycareServices,
        selectedItems: selectedDaycareServices,
        onSelectedItemsChange: setSelectedDaycareServices,
        filterFn: (service, query) => service.name.toLowerCase().includes(query.toLowerCase())
    });

    const boardingSelection = useItemSelection({
        items: boardingServices,
        selectedItems: selectedBoardingServices,
        onSelectedItemsChange: setSelectedBoardingServices,
        filterFn: (service, query) => service.name.toLowerCase().includes(query.toLowerCase())
    });

    const transportationSelection = useItemSelection({
        items: transportationServices,
        selectedItems: selectedTransportationServices,
        onSelectedItemsChange: setSelectedTransportationServices,
        filterFn: (service, query) => service.name.toLowerCase().includes(query.toLowerCase())
    });

    const evaluationSelection = useItemSelection({
        items: evaluationServices,
        selectedItems: selectedEvaluationServices,
        onSelectedItemsChange: setSelectedEvaluationServices,
        filterFn: (service, query) => service.name.toLowerCase().includes(query.toLowerCase())
    });

    const addonsSelection = useItemSelection({
        items: addons,
        selectedItems: selectedAddons,
        onSelectedItemsChange: setSelectedAddons,
        filterFn: (addon, query) => addon.name.toLowerCase().includes(query.toLowerCase())
    });

    const productsSelection = useItemSelection({
        items: products,
        selectedItems: selectedProducts,
        onSelectedItemsChange: setSelectedProducts,
        filterFn: (product, query) => {
            const productName = product.businessProduct?.name || product.name || '';
            return productName.toLowerCase().includes(query.toLowerCase());
        }
    });

    // Handler for TieredValueTypeSelector onChange
    const handleTieredDiscountChange = React.useCallback(
        (values: Array<{ type: 'amount' | 'percentage'; value: number; label: string }>) => {
            if (values.length === 0) return;

            // Extract type from first value (all have same type)
            setTieredDiscountType(values[0].type);

            // Extract just the values
            setTieredDiscountValues(values.map(v => v.value));
        },
        [setTieredDiscountType, setTieredDiscountValues]
    );

    // Convert state to TieredValueTypeSelector format
    const tieredValueTypeSelectorValues = React.useMemo(() => {
        return tieredDiscountValues.map(value => ({
            type: tieredDiscountType,
            value,
            label: '' // Label is auto-generated by component
        }));
    }, [tieredDiscountType, tieredDiscountValues]);

    const { configs: serviceCategoryConfigs, emptyServices } = useServiceCategoryConfigs({
        hasGrooming,
        hasDaycare,
        hasBoarding,
        hasTransportation,
        hasEvaluation,
        groomingServices,
        daycareServices,
        boardingServices,
        transportationServices,
        evaluationServices,
        addons,
        products,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedAddons,
        selectedProducts,
        groomingSelection,
        daycareSelection,
        boardingSelection,
        transportationSelection,
        evaluationSelection,
        addonsSelection,
        productsSelection,
        selectedTransportationServices,
        selectedEvaluationServices
    });

    const drawerConfigs = useDrawerConfigs({
        groomingSelection,
        groomingServices,
        selectedGroomingServices,
        daycareSelection,
        daycareServices,
        selectedDaycareServices,
        boardingSelection,
        boardingServices,
        selectedBoardingServices,
        transportationSelection,
        transportationServices,
        selectedTransportationServices,
        evaluationSelection,
        evaluationServices,
        selectedEvaluationServices,
        addonsSelection,
        addons,
        selectedAddons,
        productsSelection,
        products,
        selectedProducts,
        serviceDisplay,
        addonDisplay,
        productDisplay
    });

    const handleSave = React.useCallback(async () => {
        if (!isFormValid) return;

        // Map ValueTypeSelectorValue type to DiscountType enum
        const mappedDiscountType =
            applyDiscountTo === 'Tiered'
                ? tieredDiscountType === 'percentage'
                    ? DiscountType.PERCENTAGE
                    : DiscountType.FIXED
                : discountValue.type === 'percentage'
                ? DiscountType.PERCENTAGE
                : DiscountType.FIXED;

        // Build discount values based on applyDiscountTo selection
        // Backend expects percentage values as-is (e.g., 10 for 10%)
        // Backend will convert to decimal format for storage
        const convertToBackendValue = (value: number): number => {
            return value;
        };

        const values: { [key: string]: number } = {};

        // When using customer tags, backend requires ONLY 'all_pets' in values
        if (discountType === 'Client Tag' || applyDiscountTo === 'All Pets') {
            values.allPets = convertToBackendValue(discountValue.value);
        } else if (applyDiscountTo === 'Additional Pet') {
            values.additionalPets = convertToBackendValue(discountValue.value);
        } else if (applyDiscountTo === 'Tiered') {
            // Backend expects numeric keys: '0', '1', '2', etc.
            tieredDiscountValues.forEach((value, index) => {
                values[String(index)] = convertToBackendValue(value);
            });
        }

        // Build automatic discount items attributes
        const currentItemsAttributes: ConfiguredDiscountItemAttribute[] = [
            ...selectedGroomingServices
                .filter(service => service.id !== undefined)
                .map(service => ({
                    discountableId: service.id as number,
                    discountableType: DiscountableType.SERVICE
                })),
            ...selectedDaycareServices
                .filter(service => service.id !== undefined)
                .map(service => ({
                    discountableId: service.id as number,
                    discountableType: DiscountableType.SERVICE
                })),
            ...selectedBoardingServices
                .filter(service => service.id !== undefined)
                .map(service => ({
                    discountableId: service.id as number,
                    discountableType: DiscountableType.SERVICE
                })),
            ...selectedTransportationServices
                .filter(service => service.id !== undefined)
                .map(service => ({
                    discountableId: service.id as number,
                    discountableType: DiscountableType.SERVICE
                })),
            ...selectedEvaluationServices
                .filter(service => service.id !== undefined)
                .map(service => ({
                    discountableId: service.id as number,
                    discountableType: DiscountableType.SERVICE
                })),
            ...selectedAddons
                .filter(addon => addon.id !== undefined)
                .map(addon => ({
                    discountableId: addon.id as number,
                    discountableType: DiscountableType.ADDON_SERVICE
                })),
            ...selectedProducts
                .filter(product => product.businessProduct?.productId !== undefined)
                .map(product => ({
                    discountableId: product.businessProduct?.productId as number,
                    discountableType: DiscountableType.BUSINESS_PRODUCT
                }))
        ];

        // When editing, use convertToEditItemsAttributes to handle add/keep/delete logic
        const configuredDiscountItemsAttributes =
            editDiscount?.id && originalDiscount
                ? convertToEditItemsAttributes(originalDiscount, currentItemsAttributes)
                : currentItemsAttributes;

        // Build SaveConfiguredDiscount object
        // For customer tags, ensure values object has ONLY all_pets
        const finalValues =
            discountType === 'Client Tag' ? { allPets: convertToBackendValue(discountValue.value) } : values;

        const discountToSave: SaveConfiguredDiscount = {
            id: editDiscount?.id,
            name: discountName,
            discountType: mappedDiscountType,
            values: finalValues,
            active: true,
            applicationType:
                discountType === 'Client Tag'
                    ? ApplicationType.CUSTOMER_TAG
                    : applyDiscountTo === 'Tiered'
                    ? ('tiered' as ApplicationType)
                    : ApplicationType.ADDITIONAL_PETS,
            customerTagList: discountType === 'Client Tag' ? selectedClientTags.map(tag => String(tag.id)) : [],
            configuredDiscountItemsAttributes
        };

        // Use saveDiscount from context
        try {
            await saveDiscountFromContext(discountToSave);
            onSuccess(discountToSave);
        } catch (error) {
            // Error is handled by the provider
        }
    }, [
        isFormValid,
        discountName,
        discountType,
        discountValue,
        applyDiscountTo,
        tieredDiscountType,
        tieredDiscountValues,
        selectedClientTags,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedTransportationServices,
        selectedEvaluationServices,
        selectedAddons,
        selectedProducts,
        editDiscount,
        originalDiscount,
        saveDiscountFromContext,
        onSuccess
    ]);

    return (
        <>
            <OverFullWindow
                title={
                    isViewMode
                        ? 'View Automatic Discount'
                        : editDiscount
                        ? 'Editing Automatic Discount'
                        : 'Adding Automatic Discount'
                }
                onClose={() => (isViewMode ? onClose() : setShowExitPrompt(true))}
            >
                <>
                    {showExitPrompt && (
                        <ExitWithoutSavingModal
                            onDiscard={() => onClose?.()}
                            onKeepEditing={() => setShowExitPrompt(false)}
                        />
                    )}
                    <div className={styles.container}>
                        <div className={styles.form}>
                            <RadioGroup
                                label="Discount Type"
                                value={discountType}
                                onChange={isViewMode ? undefined : setDiscountType}
                                options={[
                                    {
                                        value: 'Multi Pet Discount',
                                        inlineLabel: 'Multi Pet'
                                    },
                                    {
                                        value: 'Client Tag',
                                        inlineLabel: 'Client Tag'
                                    }
                                ]}
                                className={isViewMode ? styles.disabledRadioOptions : undefined}
                            />
                            {discountType === 'Client Tag' && (
                                <div className={styles.field}>
                                    <Label required>Client Tags</Label>
                                    <p>Only clients with these tags can redeem.</p>

                                    <TagsSelector
                                        tags={clientTags}
                                        selected={selectedClientTags}
                                        addButtonColor="black"
                                        onSelect={tag =>
                                            setSelectedClientTags([
                                                ...selectedClientTags,
                                                clientTags.find(clientTag => clientTag.id === tag)!
                                            ])
                                        }
                                        onSelectAll={() => setSelectedClientTags(clientTags)}
                                        onRemove={tag =>
                                            setSelectedClientTags(
                                                selectedClientTags.filter(selectedTag => selectedTag.id !== tag)
                                            )
                                        }
                                        disabled={isViewMode}
                                    />
                                </div>
                            )}

                            <Input
                                label="Discount name"
                                placeholder="Ex. Multi pet boarding"
                                value={discountName}
                                onChange={event => setDiscountName(event.target.value)}
                                required
                                disabled={isViewMode}
                            />

                            <div className={styles.field}>
                                <Label required>Select Services</Label>
                                <p>Apply discount to services, add-ons and products.</p>

                                <div className={styles.serviceCategories}>
                                    {isServicesLoading ? (
                                        <>
                                            {Array.from({ length: 5 }).map((_, index) => (
                                                <ServiceCategorySkeleton key={index} />
                                            ))}
                                        </>
                                    ) : emptyServices ? (
                                        <p>
                                            There are no services or products eligible for discounts. Please configure
                                            at least one category first.
                                        </p>
                                    ) : (
                                        serviceCategoryConfigs.map(config =>
                                            config.show ? (
                                                <ServiceCategorySelector
                                                    key={config.id}
                                                    label={config.label}
                                                    selectedCount={config.selectedCount}
                                                    emptyMessage={config.emptyMessage}
                                                    onClick={config.onOpen}
                                                    disabled={isViewMode}
                                                />
                                            ) : null
                                        )
                                    )}
                                </div>
                            </div>
                            <div>
                                {discountType !== 'Client Tag' && (
                                    <>
                                        <RadioGroup
                                            value={applyDiscountTo}
                                            onChange={isViewMode ? undefined : setApplyDiscountTo}
                                            label="Apply Discount To"
                                            options={[
                                                {
                                                    value: 'Additional Pet',
                                                    inlineLabel: 'Additional Pet'
                                                },
                                                {
                                                    value: 'Tiered',
                                                    inlineLabel: 'Tiered'
                                                },
                                                {
                                                    value: 'All Pets',
                                                    inlineLabel: 'All Pets'
                                                }
                                            ]}
                                            className={isViewMode ? styles.disabledRadioOptions : undefined}
                                        />
                                        {applyDiscountTo === 'Additional Pet' && (
                                            <p className={styles.appliesToDescription}>
                                                Each additional pet after the first gets the same discount.
                                            </p>
                                        )}
                                        {applyDiscountTo === 'Tiered' && (
                                            <p className={styles.appliesToDescription}>
                                                Set different discount levels for the 2nd, 3rd, and 4th+ pets.
                                            </p>
                                        )}
                                        {applyDiscountTo === 'All Pets' && (
                                            <p className={styles.appliesToDescription}>
                                                Same discount applied to all pets in the booking.
                                            </p>
                                        )}
                                    </>
                                )}

                                {discountType === 'Client Tag' && (
                                    <div className={styles.field}>
                                        <Label>Discount</Label>
                                        <p>This automatic discount will apply to all tags and services selected.</p>
                                    </div>
                                )}

                                {['Additional Pet', 'All Pets'].includes(applyDiscountTo) && (
                                    <ValueTypeSelector
                                        value={discountValue}
                                        onChange={setDiscountValue}
                                        className={styles.valueTypeSelectorWidth}
                                        disabled={isViewMode}
                                    />
                                )}
                                {applyDiscountTo === 'Tiered' && discountType !== 'Client Tag' && (
                                    <TieredValueTypeSelector
                                        rowLabel="Pet"
                                        initialRowCount={4}
                                        values={tieredValueTypeSelectorValues}
                                        errors={tierErrors}
                                        onChange={handleTieredDiscountChange}
                                        disabled={isViewMode}
                                    />
                                )}
                            </div>

                            {!isViewMode && (
                                <Button
                                    size="large"
                                    label="Save"
                                    className={styles.submitButton}
                                    disabled={!isFormValid}
                                    noColorOnDisabled={true}
                                    onClick={handleSave}
                                />
                            )}
                        </div>
                    </div>
                </>
            </OverFullWindow>

            {drawerConfigs.map(config => (
                <ItemSelectionDrawer
                    key={config.id}
                    isOpen={config.selection.isOpen}
                    title={config.title}
                    items={config.items}
                    selectedItems={config.selectedItems}
                    tempSelectedItems={config.selection.tempSelectedItems}
                    searchQuery={config.selection.searchQuery}
                    filteredItems={config.selection.filteredItems}
                    selectAllLabel={config.selectAllLabel}
                    searchPlaceholder={config.searchPlaceholder}
                    itemDisplay={config.itemDisplay}
                    disabled={isViewMode}
                    onClose={config.selection.closeDrawer}
                    onSearchChange={config.selection.setSearchQuery}
                    onTempSelectionChange={config.selection.setTempSelectedItems}
                    onApply={config.selection.handleApply}
                    onCancel={config.selection.handleCancel}
                />
            ))}
        </>
    );
};
